"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import Image from "next/image";
import { useAuth } from "@/hooks/useAuth";
import { apiFetch } from "@/lib/api/client";
import type { SkillNotification } from "@/app/api/notifications/skill/route";

const POLL_INTERVAL = 5000;
const DISPLAY_DURATION = 4500;
const TRANSITION_MS = 350;

const TYPE_ICON: Record<string, string> = {
  spirit: "✨",
  material: "💎",
  hand_limit: "🖐️",
  adventure_limit: "🗺️",
  background: "🖼️",
  novel: "📖",
};

export default function SkillUnlockToast() {
  const { user, isLoggedIn } = useAuth();
  const [current, setCurrent] = useState<SkillNotification | null>(null);
  const [visible, setVisible] = useState(false);
  const queueRef = useRef<SkillNotification[]>([]);
  const isShowingRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const showNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      isShowingRef.current = false;
      setCurrent(null);
      return;
    }
    const next = queueRef.current.shift()!;
    setCurrent(next);
    // 少し遅らせてトランジションを発火
    nextTimerRef.current = setTimeout(() => setVisible(true), 30);

    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
      nextTimerRef.current = setTimeout(showNext, TRANSITION_MS + 50);
    }, DISPLAY_DURATION);
  }, []);

  const enqueue = useCallback(
    (notifications: SkillNotification[]) => {
      if (notifications.length === 0) return;
      queueRef.current.push(...notifications);
      if (!isShowingRef.current) {
        isShowingRef.current = true;
        showNext();
      }
    },
    [showNext],
  );

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await apiFetch<{ notifications: SkillNotification[] }>(
        "/api/notifications/skill",
        token,
      );
      if (res.notifications?.length > 0) enqueue(res.notifications);
    } catch {
      // 通知取得失敗は無視
    }
  }, [user, enqueue]);

  useEffect(() => {
    if (!isLoggedIn || !user) return;
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    };
  }, [isLoggedIn, user, poll]);

  if (!current) return null;

  const hasImage =
    current.imageUrl &&
    (current.rewardType === "spirit" || current.rewardType === "material");

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[200] flex justify-center pointer-events-none transition-all duration-[350ms] ease-out ${
        visible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      }`}
    >
      <div className="mt-3 mx-4 pointer-events-auto">
        <div className="flex items-center gap-3 bg-gray-950/95 backdrop-blur-md border border-violet-500/50 rounded-2xl shadow-2xl shadow-violet-900/40 px-4 py-3 min-w-[260px] max-w-xs">
          {/* 画像 or アイコン */}
          {hasImage ? (
            <div className="relative w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-black/30">
              {current.avatarType === "video" ? (
                <video
                  src={current.imageUrl}
                  className="w-full h-full object-contain"
                  muted
                  loop
                  autoPlay
                  playsInline
                />
              ) : (
                <Image
                  src={current.imageUrl}
                  alt={current.nodeName}
                  fill
                  className="object-contain"
                  sizes="56px"
                />
              )}
            </div>
          ) : (
            <div className="w-14 h-14 shrink-0 rounded-xl bg-violet-900/40 border border-violet-500/30 flex items-center justify-center text-2xl">
              {TYPE_ICON[current.rewardType] ?? "🎯"}
            </div>
          )}

          {/* テキスト */}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-violet-400 font-medium mb-0.5">スキルツリー解放</p>
            <p className="text-sm font-bold text-white leading-snug">{current.label}</p>
            {current.nodeName && (
              <p className="text-[10px] text-gray-400 mt-0.5 truncate">{current.nodeName}</p>
            )}
          </div>

          {/* 閉じるボタン */}
          <button
            onClick={() => {
              if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
              setVisible(false);
              nextTimerRef.current = setTimeout(showNext, TRANSITION_MS + 50);
            }}
            className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors text-lg leading-none ml-1"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
